import Breadcrumb from "components/Common/Breadcrumb";
import TableContainer from "components/Common/DataTableContainer";
import DashboardLoader from "components/Loader/DashboardLoader";
import DeleteModal from "pages/Calendar/DeleteModal";
import React, { useMemo, useState } from "react";

import FixedCostModal from "./FixedCostModal";
import { useDeleteFixedCostMutation, useGetAllFixedCostsQuery } from "store/features/fixedCosts/fixedCostsManagementApi";

const AllFixedCosts = () => {
  const { data: fixedCosts, isLoading, refetch } = useGetAllFixedCostsQuery();
  const [deleteFixedCost, { isLoading: deletingFixedCost }] = useDeleteFixedCostMutation();

  const [deleteModal, setDeleteModal] = useState(false);
  const [fixedCostModal, setFixedCostModal] = useState(false);
  const [editingFixedCost, setEditingFixedCost] = useState(null);
  const [selectedFixedCostId, setSelectedFixedCostId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter fixed costs based on search
  const filteredFixedCosts = useMemo(() => {
    if (!fixedCosts) return [];

    if (!searchTerm) return fixedCosts;

    const term = searchTerm.toLowerCase();
    return fixedCosts.filter(cost =>
      cost.title?.toLowerCase().includes(term)
    );
  }, [fixedCosts, searchTerm]);

  const showDeleteModal = id => {
    setSelectedFixedCostId(id);
    setDeleteModal(true);
  };

  const showAddModal = () => {
    setEditingFixedCost(null);
    setFixedCostModal(true);
  };

  const showEditModal = fixedCost => {
    setEditingFixedCost(fixedCost);
    setFixedCostModal(true);
  };

  const handleDeleteFixedCost = () => {
    deleteFixedCost(selectedFixedCostId);
    setDeleteModal(false);
  };

  const handleFixedCostSubmit = () => {
    setFixedCostModal(false);
    setEditingFixedCost(null);
    refetch();
  };

  const handleModalClose = () => {
    setFixedCostModal(false);
    setEditingFixedCost(null);
  };

  // Calculate total amount
  const totalAmount = useMemo(() => {
    return filteredFixedCosts.reduce((sum, cost) => sum + parseFloat(cost.amount || 0), 0);
  }, [filteredFixedCosts]);

  const columns = useMemo(() => [
    {
      header: "Title",
      accessorKey: "title",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="">{row.original.title}</div>
      ),
    },
    {
      header: "Amount",
      accessorKey: "amount",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div className=" text-success">
          {parseFloat(row.original.amount || 0).toLocaleString()}
        </div>
      ),
    },
    {
      header: "Date",
      accessorKey: "date",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="">
          {row.original.date ? new Date(row.original.date).toLocaleDateString() : "Not set"}
        </div>
      ),
    },

    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="d-flex align-items-center gap-1">
          <button
            className="btn btn-warning btn-sm"
            onClick={() => showEditModal(row.original)}
          >
            <i className="fas fa-edit"></i>
          </button>

          <button
            className="btn btn-danger btn-sm"
            onClick={() => showDeleteModal(row.original.id)}
          >
            <i className="fas fa-trash"></i>
            {/* {deletingFixedCost ? "Deleting..." : "Delete"} */}
          </button>
        </div>
      ),
    },
  ], [deletingFixedCost]);

  if (isLoading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Fixed Costs" breadcrumbItem="All Fixed Costs" />

        {/* Quick Stats */}
        {/* <div className="row mb-4">
          <div className="col-md-4">
            <div className="card bg-primary text-white">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-0">{filteredFixedCosts.length}</h4>
                    <span>Total Costs</span>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="fas fa-receipt fa-2x"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card bg-success text-white">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-0">${totalAmount.toLocaleString()}</h4>
                    <span>Total Amount</span>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="fas fa-dollar-sign fa-2x"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card bg-info text-white">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-0">
                      ${(totalAmount / 12).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </h4>
                    <span>Monthly Average</span>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="fas fa-calendar-alt fa-2x"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Search and Create Button */}
        <div className="row mb-3">
          <div className="col-md-6">
            <label className="form-label">Search</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by title..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="col-md-6 d-flex align-items-end justify-content-end">
            {/* <button
              className="btn btn-light me-2"
              onClick={() => setSearchTerm("")}
            >
              <i className="fas fa-times me-1"></i> Clear Search
            </button> */}
            <button
              className="btn btn-primary"
              onClick={showAddModal}
            >
              <i className="fas fa-plus"></i>
            </button>
          </div>
        </div>

        <TableContainer
          columns={columns}
          data={filteredFixedCosts}
          isGlobalFilter={false}
          isPagination={true}
          SearchPlaceholder="Search fixed costs..."
          pagination="pagination"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

        {filteredFixedCosts.length === 0 && (
          <div className="text-center py-5">
            <i className="fas fa-receipt fa-3x text-muted mb-3"></i>
            <h4 className="text-muted">No fixed costs found</h4>
            <p className="text-muted">
              {searchTerm ? `No fixed costs matching "${searchTerm}"` : "No fixed costs available"}
            </p>
            <button
              className="btn btn-primary"
              onClick={showAddModal}
            >
              <i className="fas fa-plus me-1"></i> Create Your First Fixed Cost
            </button>
          </div>
        )}

        {/* Fixed Cost Modal */}
        <FixedCostModal
          isOpen={fixedCostModal}
          toggle={handleModalClose}
          onSubmit={handleFixedCostSubmit}
          fixedCost={editingFixedCost}
        />

        {/* Delete Confirmation Modal */}
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteFixedCost}
          loader={deletingFixedCost}
          onCloseClick={() => setDeleteModal(false)}
        />
      </div>
    </div>
  );
};

export default AllFixedCosts;